import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
  query,
  setSearchQuery,
  handleKeyDown,
  hide as hideQuickSearch,
  handleClickSearch,
  openInitialResult,
} from '../Actions/QuickSearch.action';
import { addOverlay, removeOverlay } from '../Actions/Overlay.action';
import usePrevious from '@react-hook/previous';
import { FormControl, OutlinedInput } from '@material-ui/core';
import { MagnifyingGlass, X as CloseIcon } from 'phosphor-react';

const debouncedQuery = debounce((dispatch, text) => {
  if (text !== '') {
    dispatch(query(text));
  } else {
    dispatch(openInitialResult());
  }
}, 200);

const QuickSearch = () => {
  const {
    query,
    result,
    show,
    selectedItem,
    popularSearches,
    popularLinks,
  } = useSelector((state) => state.quickSearch);
  const products = result.products;
  const otherResult = result.results;
  const hasResult = result && products && products.length > 0;
  const searchUrl =
    global.LitiumQuickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const previousSelectedItem = usePrevious(selectedItem);

  const hide = useCallback(() => {
    dispatch(hideQuickSearch());
    dispatch(removeOverlay('search-container'));
  }, [dispatch]);

  const closeOverlay = useCallback(() => {
    hide();
  }, [hide]);

  const getInitialResult = useCallback(() => {
    if (!query) {
      dispatch(openInitialResult());
    }
  }, [query, dispatch]);

  const clickHandler = useCallback(
    (event) => {
      if (!searchContainer.current.contains(event.target)) {
        hide();
      }
    },
    [hide]
  );

  useEffect(() => {
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem !== previousSelectedItem) {
      const el = document.querySelector('.quick-search-result__item--selected');
      el &&
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [selectedItem, previousSelectedItem]);

  useEffect(() => {
    if (show) {
      dispatch(addOverlay('search-container', closeOverlay));
    } else {
      dispatch(removeOverlay('search-container'));
    }
  }, [show, dispatch, closeOverlay]);

  const inputStyles = show ? { borderRadius: '4px 4px 0 0' } : {};

  return (
    <div className="quick-search" role="search" ref={searchContainer}>
      <div className="quick-search__form" role="search">
        <FormControl
          className="quick-search__input-form-control"
          margin="dense"
        >
          <OutlinedInput
            margin="dense"
            id="quick-search-input"
            className="quick-search__input"
            style={inputStyles}
            placeholder={translate('quick-search.placeholder')}
            autoComplete="off"
            value={decodeURIComponent(query)}
            onChange={(event) => {
              const text = encodeURIComponent(event.target.value);
              dispatch(setSearchQuery(text));
              debouncedQuery(dispatch, text);
            }}
            onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
            ref={searchInput}
            onFocus={getInitialResult}
            endAdornment={
              <>
                {query && query.length > 0 && (
                  <CloseIcon
                    size={24}
                    cursor="pointer"
                    onClick={(e) => dispatch(setSearchQuery(''))}
                  />
                )}
                <MagnifyingGlass
                  size={24}
                  cursor="pointer"
                  onClick={(e) => dispatch(handleClickSearch({ searchUrl }))}
                />
              </>
            }
          />
        </FormControl>
        {show && hasResult && (
          <QuickSearchResult
            result={products}
            otherResult={otherResult}
            selectedItem={selectedItem}
            searchUrl={searchUrl}
            query={query}
            popularSearches={popularSearches}
            popularLinks={popularLinks}
          />
        )}
      </div>
    </div>
  );
};

export default QuickSearch;
